import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, Scrollbar, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/thumbs";
import "swiper/css/scrollbar";
import HoverMagnifier from "./HoverMagnifier";

interface Slide {
  img: string;
}

interface SwipperProps {
  slidesData: Slide[];
  dir: "vertical" | "horizontal";
  // Opt-in hover loupe on the main slide. Defaults off so existing call
  // sites (Create / Update listing previews) keep their current behavior.
  enableMagnifier?: boolean;
  // Touch-tap fallback: invoked with the tapped image URL so the parent
  // can open a fullscreen lightbox.
  onMainImageTap?: (src: string) => void;
}

const THUMB_SIZE = 100;
const THUMB_GAP = 10;

const SliderWithThumb: React.FC<SwipperProps> = ({
  slidesData,
  dir,
  enableMagnifier = false,
  onMainImageTap,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>();
  const mainSwiperRef = useRef<any>(null);
  const [swiperDirection, setSwiperDirection] = useState<
    "vertical" | "horizontal"
  >("horizontal");

  useEffect(() => {
    const handleResize = () => {
      setSwiperDirection(window.innerWidth <= 768 ? "horizontal" : dir);
    };

    setTimeout(() => handleResize(), 100);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      className={`slider-container ${
        dir === "horizontal" ? "horizonalSlider" : "verticalSlider"
      }`}
    >
      <Swiper
        ref={mainSwiperRef}
        modules={[Thumbs]}
        thumbs={{ swiper: thumbsSwiper }}
        className="main-swiper"
        autoHeight
      >
        {slidesData.map((slide, index) => (
          <SwiperSlide key={index}>
            {/* See SliderWithThumbVertical.tsx for why this is a div
                instead of <img> — boneyard's snapshot heuristic forces
                square images to render with 50% border-radius. The
                HoverMagnifier branch preserves the same div-based
                markup. */}
            {enableMagnifier ? (
              <HoverMagnifier
                src={slide?.img}
                alt={`Slide ${index}`}
                style={{
                  width: "100%",
                  // Square on phones, capped to 60vh on wider tablets so
                  // an aspect-ratio:1/1 wrapper doesn't render ~768x768
                  // and push the whole panel below the fold.
                  height: "min(100vw, 60vh)",
                  borderRadius: 10,
                }}
                onTap={
                  onMainImageTap
                    ? () => onMainImageTap(slide?.img)
                    : undefined
                }
              />
            ) : (
              <div
                role="img"
                aria-label={`Slide ${index}`}
                style={{
                  backgroundColor: "#eee",
                  backgroundImage: `url(${slide?.img})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center center",
                  width: "100%",
                  height: "min(100vw, 60vh)",
                  borderRadius: 10,
                }}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <Swiper
        modules={[Thumbs, Scrollbar, Mousewheel]}
        watchSlidesProgress
        onSwiper={setThumbsSwiper}
        spaceBetween={THUMB_GAP}
        slidesPerView={"auto"}
        direction={swiperDirection}
        scrollbar={{ draggable: true, hide: false }}
        mousewheel
        className="thumbs-swiper"
      >
        {slidesData.map((slide, index) => (
          <SwiperSlide
            key={index}
            style={{ width: THUMB_SIZE, height: THUMB_SIZE }}
          >
            {/* Inner background-image div sizes to 100% of the slide so
                that the mobile CSS rule which shrinks `.swiper-slide` to
                60×60 (App.css ≈7965) also shrinks the visible thumbnail.
                With a hard-coded 100×100 here, narrow phones would overflow
                each slot by 40px and visually overlap neighbours. */}
            <div
              role="img"
              aria-label={`Thumbnail ${index}`}
              style={{
                backgroundColor: "#eee",
                backgroundImage: `url(${slide?.img})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                width: "100%",
                height: "100%",
                borderRadius: 10,
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SliderWithThumb;
